import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  effects as companyEffects,
  selectors as companySelectors,
  STATE_DETAIL,
} from 'lib/entities/company';
import { selectors as profileSelectors } from 'lib/entities/profile';
import auth from 'utils/auth';
import { useRouter } from 'next/router';
import Header from 'features/company/Header';
import Chart from 'features/company/Chart';
import moment from 'moment';
import { useLivePrice } from 'global/Datafeed/UseLivePrice/useLivePrice';
import { UseLivePriceOnMessage } from 'global/Datafeed/UseLivePrice/useLivePriceReceiver';
import { LivePriceSymbolsKeyEnum } from 'global/Datafeed/UseLivePrice/liveprice.store.types';
import { showTradingModalWithAuth } from 'features/securities/AuthModal/utils';
import { AUTH_ORDER_TYPE } from 'features/securities/AuthModal/constants';

import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';
import If from 'global/ErrorHandlers/If';
import { ChartTimeFrames } from '../constants.types';
import {
  clevertapOnChangeTimeframe,
  clevertapOnClickChart,
} from './hero.clevertap';
import { HeroContainer } from './styled';
import { chartTimeframes } from '../constants';

interface CompanyChartProps {
  symbol: string;
}

function Hero({ symbol }: CompanyChartProps) {
  const dispatch = useDispatch();
  const router = useRouter();

  const {
    detail: { data: detailCompany },
    chart: { data: chartCompany, isLoading: isLoadingChart },
  } = useSelector(companySelectors);
  const { data: profileData } = useSelector(profileSelectors);
  const { country = 'ID' } = profileData;

  const [showMainChart, setShowMainChart] = useState(true);
  const [loadingFollow, setLoadingFollow] = useState<boolean>(false);
  const [timeframe, setTimeframe] = useState<ChartTimeFrames>(
    chartTimeframes[0].value,
  );
  const [companyData, setCompanyData] = useState({});

  const onExpandToggle = () => setShowMainChart(!showMainChart);

  const currentCompany = useMemo(
    () => detailCompany?.[symbol] || {},
    [detailCompany],
  );

  const handleClickChart = useCallback(
    () => clevertapOnClickChart(symbol),
    [symbol],
  );

  const handleSetTimeframe = useCallback(
    (v: ChartTimeFrames) => {
      setTimeframe(v);
      clevertapOnChangeTimeframe(symbol, v);
    },
    [symbol, setTimeframe],
  );

  const handleToggleFollow = async () => {
    // @ts-ignore
    const { watchlist_id: watchlistId } = auth.getUserAccess();
    const data = {
      watchlistId,
      companyId: currentCompany.id,
      stateToUpdate: STATE_DETAIL,
    };

    const action = currentCompany.followed
      ? companyEffects.postUnfollowCompany
      : companyEffects.postFollowCompany;
    setLoadingFollow(true);

    const follow = await dispatch(action(data));

    if (follow) {
      setLoadingFollow(false);
    }
  };

  const handleOpenCorpAction = (e) => {
    e.preventDefault();
    router.push(`symbol/${symbol}/corpaction`);
  };

  const handleBuyButton = () =>
    withMaintenanceGuard(() => {
      showTradingModalWithAuth({
        symbol,
        orderType: AUTH_ORDER_TYPE.BUY,
      });
    });

  useEffect(() => {
    if (timeframe && showMainChart) {
      const params = {
        symbol,
        timeframe,
      };

      dispatch(companyEffects.getCompanyChart(params));
    }
  }, [dispatch, symbol, showMainChart, timeframe]);

  useEffect(() => {
    if (detailCompany[symbol]) {
      setCompanyData((prevState) => ({
        ...prevState,
        ...detailCompany[symbol],
      }));
    }
  }, [detailCompany]);

  const livePriceOnMessage: UseLivePriceOnMessage = useCallback(
    ({ date, lastprice, change, percentage_change: percentage, volume }) => {
      setCompanyData((prevState) => ({
        ...prevState,
        price: lastprice,
        change,
        percentage,
        volume,
        time: moment(date).format('ddd HH:mm'),
      }));
    },
    [],
  );

  useLivePrice(
    [symbol],
    LivePriceSymbolsKeyEnum.COMPANY_HERO,
    'company',
    livePriceOnMessage,
  );

  return (
    <HeroContainer>
      <Header
        symbol={symbol}
        company={currentCompany}
        isLoadingFollow={loadingFollow}
        onClickNotation={() => {}}
        onClickFollow={handleToggleFollow}
        onClickCorpAction={handleOpenCorpAction}
        onClickBuyButton={handleBuyButton}
        onExpandToggle={onExpandToggle}
      />
      <If condition={country === 'ID'}>
        <Chart
          symbol={symbol}
          company={companyData}
          isLoadingChart={isLoadingChart}
          chart={chartCompany?.[symbol]}
          timeframe={timeframe}
          isChartVisible={showMainChart}
          onExpand={onExpandToggle}
          onSelectTimeFrame={handleSetTimeframe}
          onClickChart={handleClickChart}
        />
      </If>
    </HeroContainer>
  );
}

export default Hero;
